// Text catalog for the Projektassistance / Project assistance service page.
// Icons and images live in src/pages/ProjektassistancePage.tsx and are aligned
// by index with the phase and value-prop arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type ValueProp = { title: string; text: string };
type Phase = { label: string; title: string; items: string[] };
type Faq = { q: string; a: string };

type ProjektassistanceStrings = {
  meta: PageMeta;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  twTitle: string;
  twDescription: string;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; p1: string; p2: string };
  why: { eyebrow: string; heading: string; props: ValueProp[] };
  approach: { eyebrow: string; heading: string; intro: string; phases: Phase[] };
  sectors: { eyebrow: string; heading: string; intro: string; items: string[] };
  faq: { eyebrow: string; heading: string; items: Faq[] };
  cta: { heading: string; body: string; button: string };
  jsonLd: { serviceType: string; name: string; description: string; audienceType: string };
};

export const projektassistanceStrings: Record<Locale, ProjektassistanceStrings> = {
  da: {
    meta: {
      title: "Projektassistance til store byggeprojekter | MI Support",
      description:
        "Erfaren projektassistance fra MI Support styrker projektledelsen på komplekse byggerier med dokumenthåndtering, mødekoordinering og rapportering.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    keywords:
      "projektassistance, projektkoordinator, projektsupport, dokumentstyring byggeri, projektadministration, byggeprojekt support, PMO",
    ogTitle: "Projektassistance | MI Support",
    ogDescription:
      "Stærk projektassistance og koordinering til komplekse byggerier, dokumentstyring, rapportering og daglig støtte til projektorganisationen.",
    twTitle: "Projektassistance | MI Support",
    twDescription: "Projektassistance og koordinering til komplekse byggerier.",
    hero: {
      eyebrow: "Vores ydelser",
      title: "Projektassistance til dit næste byggeprojekt",
      body: "MI Support stiller erfarne projektassistenter til rådighed, der styrker projektorganisationen på jeres byggeprojekt. Vi tager hånd om dokumentstyring, mødekoordinering, rapportering og den daglige administration, så projektledelsen kan fokusere på fremdrift og beslutninger.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle ydelser",
    },
    intro: {
      eyebrow: "Stærk støtte til projektorganisationen",
      heading: "Vi holder styr på detaljerne",
      p1: "På store byggeprojekter er der hundredvis af dokumenter, beslutninger, møder og aktioner, der skal håndteres hver uge. Uden en stærk projektassistance bliver overblikket hurtigt tabt, og vigtige beslutninger ender med at mangle dokumentation.",
      p2: "Vores projektassistenter indgår i jeres projektorganisation som en part af teamet. De arbejder tæt sammen med projektleder, byggeleder og bygherrerådgiver og bruger jeres egne samarbejdsplatforme som Microsoft Teams, Dalux, Byggeweb, SharePoint eller projektspecifikke PMO-værktøjer.",
    },
    why: {
      eyebrow: "Hvorfor MI Support",
      heading: "Projektassistenter der gør en forskel",
      props: [
        {
          title: "Aflastning af projektledelsen",
          text: "MI Supports projektassistenter aflaster projekt- og byggeledelsen for administrative opgaver, så ledelsen kan fokusere på fremdrift, beslutninger og dialog med bygherre og entreprenører.",
        },
        {
          title: "Struktur og sporbarhed",
          text: "Vi etablerer en gennemsigtig struktur for dokumenter, beslutninger og kommunikation. Det giver fuld sporbarhed gennem hele projektet og et solidt grundlag, hvis sager skulle udvikle sig til tvister.",
        },
        {
          title: "Erfaring med komplekse projekter",
          text: "Vores projektassistenter har understøttet projektorganisationer på bygge- og anlægsprojekter inden for industri, life science og offentlige bygherrer og forstår kravene til dokumentation og governance.",
        },
      ],
    },
    approach: {
      eyebrow: "Vores tilgang",
      heading: "Projektassistance gennem hele projektets levetid",
      intro:
        "Vi etablerer en klar struktur fra projektets start og holder den ved lige hele vejen til aflevering. Bygherre og projektledelse får ét samlet overblik over dokumenter, beslutninger og aktioner, uanset hvor komplekst projektet er.",
      phases: [
        {
          label: "Fase 01",
          title: "Opstart & struktur",
          items: [
            "Etablering af projektkontor og governance",
            "Mappestruktur, navnestandarder og dokumentplan",
            "Onboarding af projektdeltagere og rådgivere",
            "Opsætning af samarbejdsplatforme (Teams, Dalux, Byggeweb)",
          ],
        },
        {
          label: "Fase 02",
          title: "Drift & koordinering",
          items: [
            "Mødeplanlægning, dagsordener og referater",
            "Dokumentstyring, versionskontrol og distribution",
            "Aktionslister, beslutningslog og opfølgning",
            "Koordinering mellem rådgivere, entreprenører og bygherre",
          ],
        },
        {
          label: "Fase 03",
          title: "Rapportering & afslutning",
          items: [
            "Statusrapportering til ledelse og styregruppe",
            "Aflevering af driftsmateriale og as-built dokumentation",
            "Arkivering og overdragelse til drift",
            "Lessons learned og evaluering",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Brancher",
      heading: "Erfaring med assistance på krævende byggerier",
      intro:
        "Vores projektassistenter har understøttet projektorganisationer på nogle af Danmarks mest komplekse byggeprojekter, hvor dokumentation, governance og kommunikation er afgørende for et godt forløb.",
      items: [
        "Life science & pharma (GMP)",
        "Industri- og produktionsanlæg",
        "Logistik og datacentre",
        "Kontor- og hovedsædebyggeri",
        "Offentlige bygherrer",
        "Infrastruktur og forsyning",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Ofte stillede spørgsmål om projektassistance",
      items: [
        {
          q: "Hvad laver en projektassistent på et byggeprojekt?",
          a: "En projektassistent understøtter projektledelsen med alt det administrative og koordinerende arbejde: dokumentstyring, mødekoordinering, referater, aktionslister, rapportering og opfølgning. Det giver projektledelsen mere tid til de tunge faglige beslutninger.",
        },
        {
          q: "Hvornår giver det mening at tilkøbe projektassistance?",
          a: "Når projektet har mange interessenter, en stor mængde dokumenter eller en kompleks governance-struktur. Også når projektledelsen er presset på tid, eller når bygherre ønsker stærkere sporbarhed, struktur og rapportering, er en dedikeret projektassistent fra MI Support en god investering.",
        },
        {
          q: "Arbejder I i vores egne systemer?",
          a: "Ja. Vores projektassistenter arbejder typisk i bygherrens egne samarbejdsplatforme, fx Microsoft Teams, SharePoint, Dalux, Byggeweb, RIB, Ajour eller dedikerede PMO-værktøjer, og rapporterer i jeres egne formater og standarder.",
        },
        {
          q: "Kan I overtage et projekt midt i forløbet?",
          a: "Ja. Vores projektassistenter er vant til at gå ind i igangværende projekter, danne sig overblik hurtigt og skabe struktur i materialet. Vi laver typisk en kort onboarding sammen med projektledelsen og er klar til at understøtte fra første uge.",
        },
      ],
    },
    cta: {
      heading: "Skal vi styrke jeres projektorganisation?",
      body: "Står I overfor et stort byggeprojekt, eller har jeres nuværende projekt brug for stærkere koordinering og struktur? MI Supports projektassistenter er klar til en uforpligtende snak om jeres situation og behov.",
      button: "Kontakt os og hør mere",
    },
    jsonLd: {
      serviceType: "Projektassistance og projektkoordinering",
      name: "Projektassistance",
      description:
        "Professionel projektassistance til store og komplekse byggeprojekter, dokumenthåndtering, mødekoordinering, rapportering og support til projektorganisationen.",
      audienceType: "Bygherrer, industri, life science, offentlige bygherrer",
    },
  },

  en: {
    meta: {
      title: "Project assistance for construction projects | MI Support",
      description:
        "Experienced project assistance from MI Support strengthens project management on complex builds with document handling, coordination and reporting.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    keywords:
      "project assistance, project coordinator, project support, document management construction, project administration, construction project support, PMO",
    ogTitle: "Project assistance | MI Support",
    ogDescription:
      "Strong project assistance and coordination for complex builds, document management, reporting and daily support for the project organisation.",
    twTitle: "Project assistance | MI Support",
    twDescription: "Project assistance and coordination for complex builds.",
    hero: {
      eyebrow: "Our services",
      title: "Project assistance for your next construction project",
      body: "MI Support provides experienced project assistants who strengthen the project organisation on your construction project. We take care of document management, meeting coordination, reporting and the daily administration, so the project management can focus on momentum and decisions.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all services",
    },
    intro: {
      eyebrow: "Strong support for the project organisation",
      heading: "We keep track of the details",
      p1: "On large construction projects there are hundreds of documents, decisions, meetings and actions that have to be handled every week. Without strong project assistance the overview is quickly lost, and important decisions end up lacking documentation.",
      p2: "Our project assistants join your project organisation as part of the team. They work closely with the project manager, construction manager and client consultant and use your own collaboration platforms such as Microsoft Teams, Dalux, Byggeweb, SharePoint or project-specific PMO tools.",
    },
    why: {
      eyebrow: "Why MI Support",
      heading: "Project assistants who make a difference",
      props: [
        {
          title: "Relieving the project management",
          text: "MI Support's project assistants relieve project and construction management of administrative tasks, so the management can focus on momentum, decisions and dialogue with the client and contractors.",
        },
        {
          title: "Structure and traceability",
          text: "We establish a transparent structure for documents, decisions and communication. This provides full traceability throughout the project and a solid foundation should matters develop into disputes.",
        },
        {
          title: "Experience with complex projects",
          text: "Our project assistants have supported project organisations on construction and civil engineering projects within industry, life science and public clients and understand the requirements for documentation and governance.",
        },
      ],
    },
    approach: {
      eyebrow: "Our approach",
      heading: "Project assistance throughout the project's life cycle",
      intro:
        "We establish a clear structure from the start of the project and maintain it all the way to handover. The client and project management get one combined overview of documents, decisions and actions, no matter how complex the project is.",
      phases: [
        {
          label: "Phase 01",
          title: "Start-up & structure",
          items: [
            "Establishment of project office and governance",
            "Folder structure, naming standards and document plan",
            "Onboarding of project participants and advisors",
            "Setup of collaboration platforms (Teams, Dalux, Byggeweb)",
          ],
        },
        {
          label: "Phase 02",
          title: "Operation & coordination",
          items: [
            "Meeting planning, agendas and minutes",
            "Document management, version control and distribution",
            "Action lists, decision log and follow-up",
            "Coordination between advisors, contractors and client",
          ],
        },
        {
          label: "Phase 03",
          title: "Reporting & closure",
          items: [
            "Status reporting to management and steering committee",
            "Handover of operating material and as-built documentation",
            "Archiving and handover to operations",
            "Lessons learned and evaluation",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Sectors",
      heading: "Experience with assistance on demanding builds",
      intro:
        "Our project assistants have supported project organisations on some of Denmark's most complex construction projects, where documentation, governance and communication are crucial for a successful process.",
      items: [
        "Life science & pharma (GMP)",
        "Industrial and production facilities",
        "Logistics and data centres",
        "Office and headquarters construction",
        "Public clients",
        "Infrastructure and utilities",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Frequently asked questions about project assistance",
      items: [
        {
          q: "What does a project assistant do on a construction project?",
          a: "A project assistant supports the project management with all the administrative and coordinating work: document management, meeting coordination, minutes, action lists, reporting and follow-up. This gives the project management more time for the heavy professional decisions.",
        },
        {
          q: "When does it make sense to add project assistance?",
          a: "When the project has many stakeholders, a large volume of documents or a complex governance structure. Also when the project management is under time pressure, or when the client wants stronger traceability, structure and reporting, a dedicated project assistant from MI Support is a good investment.",
        },
        {
          q: "Do you work in our own systems?",
          a: "Yes. Our project assistants typically work in the client's own collaboration platforms, e.g. Microsoft Teams, SharePoint, Dalux, Byggeweb, RIB, Ajour or dedicated PMO tools, and report in your own formats and standards.",
        },
        {
          q: "Can you take over a project mid-way?",
          a: "Yes. Our project assistants are used to stepping into ongoing projects, getting an overview quickly and creating structure in the material. We typically do a short onboarding together with the project management and are ready to support from the first week.",
        },
      ],
    },
    cta: {
      heading: "Shall we strengthen your project organisation?",
      body: "Are you facing a large construction project, or does your current project need stronger coordination and structure? MI Support's project assistants are ready for a no-obligation chat about your situation and needs.",
      button: "Contact us to learn more",
    },
    jsonLd: {
      serviceType: "Project assistance and project coordination",
      name: "Project assistance",
      description:
        "Professional project assistance for large and complex construction projects, document handling, meeting coordination, reporting and support for the project organisation.",
      audienceType: "Clients, industry, life science, public clients",
    },
  },

  de: {
    meta: {
      title: "Projektassistenz für große Bauprojekte | MI Support",
      description:
        "Erfahrene Projektassistenz von MI Support stärkt die Projektleitung bei komplexen Bauvorhaben mit Dokumentenmanagement, Koordination und Berichtswesen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    keywords:
      "Projektassistenz, Projektkoordinator, Projektsupport, Dokumentenmanagement Bau, Projektadministration, Bauprojekt-Support, PMO",
    ogTitle: "Projektassistenz | MI Support",
    ogDescription:
      "Starke Projektassistenz und Koordination für komplexe Bauvorhaben, Dokumentenmanagement, Berichtswesen und tägliche Unterstützung der Projektorganisation.",
    twTitle: "Projektassistenz | MI Support",
    twDescription: "Projektassistenz und Koordination für komplexe Bauvorhaben.",
    hero: {
      eyebrow: "Unsere Leistungen",
      title: "Projektassistenz für Ihr nächstes Bauprojekt",
      body: "MI Support stellt erfahrene Projektassistenten zur Verfügung, die die Projektorganisation Ihres Bauprojekts stärken. Wir kümmern uns um Dokumentenmanagement, Besprechungskoordination, Berichtswesen und die tägliche Administration, damit sich die Projektleitung auf Fortschritt und Entscheidungen konzentrieren kann.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Leistungen ansehen",
    },
    intro: {
      eyebrow: "Starke Unterstützung der Projektorganisation",
      heading: "Wir behalten die Details im Blick",
      p1: "Bei großen Bauprojekten gibt es Hunderte von Dokumenten, Entscheidungen, Besprechungen und Aktionen, die jede Woche bearbeitet werden müssen. Ohne eine starke Projektassistenz geht der Überblick schnell verloren, und wichtige Entscheidungen bleiben am Ende ohne Dokumentation.",
      p2: "Unsere Projektassistenten werden Teil Ihrer Projektorganisation und gehören zum Team. Sie arbeiten eng mit Projektleiter, Bauleiter und Bauherrenberater zusammen und nutzen Ihre eigenen Kollaborationsplattformen wie Microsoft Teams, Dalux, Byggeweb, SharePoint oder projektspezifische PMO-Werkzeuge.",
    },
    why: {
      eyebrow: "Warum MI Support",
      heading: "Projektassistenten, die einen Unterschied machen",
      props: [
        {
          title: "Entlastung der Projektleitung",
          text: "Die Projektassistenten von MI Support entlasten Projekt- und Bauleitung von administrativen Aufgaben, sodass sich die Leitung auf Fortschritt, Entscheidungen und den Dialog mit Bauherrn und Auftragnehmern konzentrieren kann.",
        },
        {
          title: "Struktur und Nachverfolgbarkeit",
          text: "Wir schaffen eine transparente Struktur für Dokumente, Entscheidungen und Kommunikation. Das sorgt für volle Nachverfolgbarkeit während des gesamten Projekts und eine solide Grundlage, falls sich Vorgänge zu Streitigkeiten entwickeln sollten.",
        },
        {
          title: "Erfahrung mit komplexen Projekten",
          text: "Unsere Projektassistenten haben Projektorganisationen bei Bau- und Anlagenprojekten in Industrie, Life Science und bei öffentlichen Bauherren unterstützt und verstehen die Anforderungen an Dokumentation und Governance.",
        },
      ],
    },
    approach: {
      eyebrow: "Unser Ansatz",
      heading: "Projektassistenz über den gesamten Projektlebenszyklus",
      intro:
        "Wir schaffen von Projektbeginn an eine klare Struktur und pflegen sie bis zur Übergabe. Bauherr und Projektleitung erhalten einen einzigen, zusammenhängenden Überblick über Dokumente, Entscheidungen und Aktionen, egal wie komplex das Projekt ist.",
      phases: [
        {
          label: "Phase 01",
          title: "Start & Struktur",
          items: [
            "Einrichtung von Projektbüro und Governance",
            "Ordnerstruktur, Namensstandards und Dokumentenplan",
            "Onboarding von Projektbeteiligten und Beratern",
            "Einrichtung von Kollaborationsplattformen (Teams, Dalux, Byggeweb)",
          ],
        },
        {
          label: "Phase 02",
          title: "Betrieb & Koordination",
          items: [
            "Besprechungsplanung, Tagesordnungen und Protokolle",
            "Dokumentenmanagement, Versionskontrolle und Verteilung",
            "Aktionslisten, Entscheidungslog und Nachverfolgung",
            "Koordination zwischen Beratern, Auftragnehmern und Bauherrn",
          ],
        },
        {
          label: "Phase 03",
          title: "Berichtswesen & Abschluss",
          items: [
            "Statusberichte an Leitung und Lenkungsausschuss",
            "Übergabe von Betriebsunterlagen und As-built-Dokumentation",
            "Archivierung und Übergabe an den Betrieb",
            "Lessons Learned und Evaluierung",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Branchen",
      heading: "Erfahrung mit Assistenz bei anspruchsvollen Bauvorhaben",
      intro:
        "Unsere Projektassistenten haben Projektorganisationen bei einigen der komplexesten Bauprojekte Dänemarks unterstützt, bei denen Dokumentation, Governance und Kommunikation entscheidend für einen erfolgreichen Verlauf sind.",
      items: [
        "Life Science & Pharma (GMP)",
        "Industrie- und Produktionsanlagen",
        "Logistik und Rechenzentren",
        "Büro- und Hauptsitzbauten",
        "Öffentliche Bauherren",
        "Infrastruktur und Versorgung",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Häufig gestellte Fragen zur Projektassistenz",
      items: [
        {
          q: "Was macht ein Projektassistent bei einem Bauprojekt?",
          a: "Ein Projektassistent unterstützt die Projektleitung bei allen administrativen und koordinierenden Aufgaben: Dokumentenmanagement, Besprechungskoordination, Protokolle, Aktionslisten, Berichtswesen und Nachverfolgung. So bleibt der Projektleitung mehr Zeit für die schweren fachlichen Entscheidungen.",
        },
        {
          q: "Wann ist es sinnvoll, Projektassistenz hinzuzukaufen?",
          a: "Wenn das Projekt viele Beteiligte, eine große Menge an Dokumenten oder eine komplexe Governance-Struktur hat. Auch wenn die Projektleitung unter Zeitdruck steht oder der Bauherr stärkere Nachverfolgbarkeit, Struktur und Berichtswesen wünscht, ist ein dedizierter Projektassistent von MI Support eine gute Investition.",
        },
        {
          q: "Arbeiten Sie in unseren eigenen Systemen?",
          a: "Ja. Unsere Projektassistenten arbeiten in der Regel in den eigenen Kollaborationsplattformen des Bauherrn, z. B. Microsoft Teams, SharePoint, Dalux, Byggeweb, RIB, Ajour oder dedizierten PMO-Werkzeugen, und berichten in Ihren eigenen Formaten und Standards.",
        },
        {
          q: "Können Sie ein Projekt mitten im Verlauf übernehmen?",
          a: "Ja. Unsere Projektassistenten sind es gewohnt, in laufende Projekte einzusteigen, sich schnell einen Überblick zu verschaffen und Struktur in das Material zu bringen. Wir führen in der Regel ein kurzes Onboarding gemeinsam mit der Projektleitung durch und sind ab der ersten Woche einsatzbereit.",
        },
      ],
    },
    cta: {
      heading: "Sollen wir Ihre Projektorganisation stärken?",
      body: "Stehen Sie vor einem großen Bauprojekt oder benötigt Ihr aktuelles Projekt stärkere Koordination und Struktur? Die Projektassistenten von MI Support sind bereit für ein unverbindliches Gespräch über Ihre Situation und Bedürfnisse.",
      button: "Kontaktieren Sie uns für mehr Informationen",
    },
    jsonLd: {
      serviceType: "Projektassistenz und Projektkoordination",
      name: "Projektassistenz",
      description:
        "Professionelle Projektassistenz für große und komplexe Bauprojekte, Dokumentenmanagement, Besprechungskoordination, Berichtswesen und Unterstützung der Projektorganisation.",
      audienceType: "Bauherren, Industrie, Life Science, öffentliche Bauherren",
    },
  },
};
